import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { contactInfo } from '../mockData';
import { MapPin, Phone, Mail, Clock, ExternalLink, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { sendContactMessage } from '../lib/contact';
import { createStaggerContainer, fadeInLeft, fadeInRight, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.3 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18, delayChildren: 0.1 });

  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await sendContactMessage(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setErrorMessage(error.message);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.section id="contact" className="py-12 sm:py-16 lg:py-20 bg-[#F5F0E8]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.div className="flex items-center justify-center gap-3 mb-4" variants={fadeInUp}>
            <motion.span
              className="h-px w-12 bg-gradient-to-r from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#a5400e] font-medium">Contact</span>
            <motion.span
              className="h-px w-12 bg-gradient-to-l from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          </motion.div>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5c2810] mb-3 sm:mb-4 relative inline-block" variants={fadeInUp}>
            Contactez-moi
            <motion.span
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#a5400e] to-transparent rounded-full"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#7a4a3a] max-w-2xl mx-auto px-4" variants={fadeInUp}>
            Des questions sur mes services, besoin d'informations ou pour prendre rendez-vous ? N'hésitez pas à me contacter
          </motion.p>
        </motion.div>

        <motion.div className="grid lg:grid-cols-2 gap-8 lg:gap-12" variants={containerVariants}>
          {/* Left Column - Contact Info */}
          <motion.div variants={createStaggerContainer({ staggerChildren: 0.12 })}>
            <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5c2810] mb-6 sm:mb-8" variants={fadeInLeft}>
              Informations de contact
            </motion.h3>

            <motion.div className="space-y-4 sm:space-y-6" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
              <motion.div variants={scaleIn}>
                <Card className="group border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <MapPin className="text-[#a5400e]" size={20} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5c2810] mb-2">Adresse</h4>
                        <p className="text-sm text-[#7a4a3a] mb-1">{contactInfo.address.street}</p>
                        <p className="text-sm text-[#7a4a3a] mb-3">{contactInfo.address.city}</p>
                        <motion.a
                          href={contactInfo.address.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Voir l'adresse sur Google Maps (s'ouvre dans une nouvelle fenêtre)"
                          className="inline-flex items-center space-x-2 text-[#a5400e] hover:text-[#7a2b0d] transition-colors duration-300 text-sm font-medium"
                          whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                        >
                          <span>Voir sur Google Maps</span>
                          <ExternalLink size={14} aria-hidden="true" />
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="group border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Phone className="text-[#a5400e]" size={20} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5c2810] mb-2">Téléphone</h4>
                        <p className="text-lg sm:text-xl font-semibold text-[#5c2810] mb-2">{contactInfo.phone.display}</p>
                        <p className="text-sm text-[#7a4a3a] mb-3">{contactInfo.phone.hours}</p>
                        <motion.a href={`tel:${contactInfo.phone.link}`} whileHover={shouldReduceMotion ? undefined : { y: -2 }}>
                          <Button size="sm" className="bg-[#a5400e] hover:bg-[#7a2b0d] text-white text-xs sm:text-sm rounded-lg">
                            Appeler maintenant
                          </Button>
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="group border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Mail className="text-[#a5400e]" size={20} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5c2810] mb-2">Email</h4>
                        <p className="text-sm text-[#7a4a3a] mb-2 break-all">{contactInfo.email.address}</p>
                        <motion.a href={`mailto:${contactInfo.email.address}`} whileHover={shouldReduceMotion ? undefined : { y: -2 }}>
                          <Button size="sm" className="bg-[#a5400e] hover:bg-[#7a2b0d] text-white text-xs sm:text-sm rounded-lg">
                            Envoyer un email
                          </Button>
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="group border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Clock className="text-[#a5400e]" size={20} aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="text-base sm:text-lg font-serif text-[#5c2810] mb-2">Horaires d'ouverture</h4>
                        <p className="text-sm text-[#7a4a3a]">{contactInfo.schedule.weekdays}</p>
                        <p className="text-sm text-[#7a4a3a]">{contactInfo.schedule.weekend}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div variants={fadeInRight}>
            {/* Titre invisible pour aligner avec la colonne de gauche */}
            <h3 className="text-2xl sm:text-3xl font-serif text-transparent mb-6 sm:mb-8 h-[36px] sm:h-[42px]">Formulaire</h3>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#a5400e]/5 via-[#d46a3e]/5 to-[#e8a87c]/5 rounded-2xl blur-2xl -z-10" />
              <motion.div
                className="relative bg-white/70 backdrop-blur-sm p-8 sm:p-10 rounded-2xl border border-[#a5400e]/10 shadow-[0_10px_30px_rgb(165,64,14,0.06)] sm:shadow-[0_20px_60px_rgb(165,64,14,0.08)]"
                variants={fadeInUp}
              >
                <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5c2810] mb-6 sm:mb-8" variants={fadeInUp}>
                  Envoyez-moi un message
                </motion.h3>

                <motion.form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
                  <motion.div variants={fadeInUp}>
                    <label htmlFor="name" className="block text-sm font-medium text-[#5c2810] mb-2">
                      Nom et Prénom
                    </label>
                    <Input
                      id="name"
                      type="text"
                      autoComplete="name"
                      minLength={2}
                      maxLength={100}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/80 border-[#a5400e]/20 focus:border-[#a5400e] rounded-xl"
                      required
                    />
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <label htmlFor="email" className="block text-sm font-medium text-[#5c2810] mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/80 border-[#a5400e]/20 focus:border-[#a5400e] rounded-xl"
                      required
                    />
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <label htmlFor="message" className="block text-sm font-medium text-[#5c2810] mb-2">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      aria-describedby="message-guidance"
                      minLength={10}
                      maxLength={5000}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-white/80 border-[#a5400e]/20 focus:border-[#a5400e] min-h-[150px] rounded-xl"
                      required
                    />
                    <p id="message-guidance" className="mt-2 text-xs sm:text-sm text-[#7a4a3a] leading-relaxed">
                      {"Pour une prise de contact, quelques mots suffisent. Merci de ne pas transmettre de données de santé ou de document médical dans ce formulaire."}
                    </p>
                  </motion.div>

                  {submitStatus && (
                    <motion.div
                      role={submitStatus === 'error' ? 'alert' : 'status'}
                      variants={fadeInUp}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-xl flex items-center gap-3 ${
                        submitStatus === 'success'
                          ? 'bg-green-50 border border-green-200 text-green-800'
                          : 'bg-red-50 border border-red-200 text-red-800'
                      }`}
                    >
                      {submitStatus === 'success' ? (
                        <>
                          <CheckCircle className="text-green-600 flex-shrink-0" size={24} aria-hidden="true" />
                          <span>Votre message a été envoyé avec succès ! Je vous répondrai rapidement.</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="text-red-600 flex-shrink-0" size={24} aria-hidden="true" />
                          <span>
                            {errorMessage}
                            {' '}<a className="underline font-medium" href={`mailto:${contactInfo.email.address}`}>M'écrire par email</a>
                          </span>
                        </>
                      )}
                    </motion.div>
                  )}

                  <motion.div variants={scaleIn}>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#a5400e] hover:bg-[#7a2b0d] text-white py-5 sm:py-6 text-sm sm:text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#a5400e]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <motion.span
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            ⏳
                          </motion.span>
                          Envoi en cours...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Send size={18} />
                          Envoyer
                        </span>
                      )}
                    </Button>
                  </motion.div>
                </motion.form>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ContactSection;
